// PWA utilities for mobile app-like functionality
import React from 'react';
import { toast } from '@/hooks/use-toast';

export interface PWAInstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export interface BeforeInstallPromptEvent extends Event {
  platforms: string[];
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void>;
}

export interface PWAConfig {
  showInstallPrompt: boolean;
  enableOfflineSupport: boolean;
  autoRefreshOnUpdate: boolean;
  notifyOnOffline: boolean;
  prefetchRoutes: string[];
}

class PWAManager {
  private installPrompt: BeforeInstallPromptEvent | null = null;
  private isStandalone = false;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private config: PWAConfig;

  constructor(config: PWAConfig) {
    this.config = config;
    this.init();
  }

  private async init() {
    // Check if running in standalone mode
    this.isStandalone = this.checkStandaloneMode();

    // Register service worker
    if ('serviceWorker' in navigator) {
      try {
        this.swRegistration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });

        this.setupServiceWorkerListeners();
      } catch (error) {
        console.error('PWA: Service Worker registration failed:', error);
      }
    }

    // Setup PWA install prompt
    if (this.config.showInstallPrompt) {
      this.setupInstallPrompt();
    }

    // Setup offline/online listeners
    if (this.config.notifyOnOffline) {
      this.setupNetworkListeners();
    }

    // Prefetch routes for faster navigation
    if (this.config.prefetchRoutes.length > 0) {
      this.prefetchRoutes(this.config.prefetchRoutes);
    }
  }

  private checkStandaloneMode(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator && (window.navigator as any).standalone) ||
      document.referrer.includes('android-app://')
    );
  }

  private setupServiceWorkerListeners() {
    if (!this.swRegistration) return;

    // Listen for service worker updates
    this.swRegistration.addEventListener('updatefound', () => {
      const newWorker = this.swRegistration!.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // Update notification disabled
          }
        });
      }
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type, data } = event.data;

      switch (type) {
        case 'UPDATE_AVAILABLE':
          // Update notification disabled
          break;
        case 'SUPPORT_REQUEST_SYNCED':
          this.handleOfflineRequestSynced(data);
          break;
        case 'DATA_SYNCED':
          break;
        default:
      }
    });
  }

  private setupInstallPrompt() {
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.installPrompt = e as BeforeInstallPromptEvent;
    });

    // Listen for successful app install
    window.addEventListener('appinstalled', () => {
      this.installPrompt = null;
      
      toast({
        title: 'App Installed!',
        description: 'MagnetFlow Portal has been added to your home screen.',
      });
    });
  }

  private setupNetworkListeners() {
    window.addEventListener('online', () => {
      
      toast({
        title: 'Back Online',
        description: 'Your connection has been restored. Syncing data...',
      });

      // Trigger background sync
      this.requestBackgroundSync('sync-offline-data');
    });

    window.addEventListener('offline', () => {
      
      if (this.config.notifyOnOffline) {
        toast({
          title: 'You\'re Offline',
          description: 'Some features are still available. Changes will sync when you\'re back online.',
          variant: 'destructive',
        });
      }
    });
  }

  // Public methods

  public async showInstallPrompt(): Promise<boolean> {
    if (!this.installPrompt) {
      return false;
    }

    try {
      await this.installPrompt.prompt();
      const choiceResult = await this.installPrompt.userChoice;
      
      
      if (choiceResult.outcome === 'accepted') {
        this.installPrompt = null;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('PWA: Error showing install prompt:', error);
      return false;
    }
  }

  public isInstallable(): boolean {
    return this.installPrompt !== null;
  }

  public isInstalledApp(): boolean {
    return this.isStandalone;
  }

  public async storeOfflineRequest(requestData: any): Promise<void> {
    if (!navigator.serviceWorker.controller) {
      throw new Error('Service worker not available');
    }

    navigator.serviceWorker.controller.postMessage({
      type: 'STORE_OFFLINE_REQUEST',
      data: requestData
    });

  }

  public async updateApp(): Promise<void> {
    if (!this.swRegistration) return;

    // Tell service worker to skip waiting
    if (this.swRegistration.waiting) {
      this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Wait for controlling and reload
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (this.config.autoRefreshOnUpdate) {
          window.location.reload();
        }
      });
    }
  }

  public async getCacheStatus(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!navigator.serviceWorker.controller) {
        reject(new Error('Service worker not available'));
        return;
      }

      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        if (event.data.type === 'CACHE_STATUS') {
          resolve(event.data.data);
        }
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_CACHE_STATUS' },
        [messageChannel.port2]
      );
    });
  }

  public async clearCache(cacheType: string = 'all'): Promise<void> {
    if (!navigator.serviceWorker.controller) return;

    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_CACHE',
      data: { cacheType }
    });

  }

  private async prefetchRoutes(routes: string[]): Promise<void> {
    if (!navigator.serviceWorker.controller) return;

    navigator.serviceWorker.controller.postMessage({
      type: 'PREFETCH_ROUTES',
      data: { routes }
    });

  }

  private async requestBackgroundSync(tag: string): Promise<void> {
    if (!this.swRegistration || !('sync' in window.ServiceWorkerRegistration.prototype)) {
      return;
    }

    try {
      await this.swRegistration.sync.register(tag);
    } catch (error) {
      console.error('PWA: Background sync registration failed:', error);
    }
  }


  private handleOfflineRequestSynced(data: any) {
    toast({
      title: 'Request Submitted',
      description: 'Your offline support request has been submitted successfully.',
    });
  }

  // Network status
  public isOnline(): boolean {
    return navigator.onLine;
  }

  public getConnectionType(): string {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    return connection?.effectiveType || 'unknown';
  }
}

// Hook for using PWA functionality
export const usePWA = (config: Partial<PWAConfig> = {}) => {
  const defaultConfig: PWAConfig = {
    showInstallPrompt: true,
    enableOfflineSupport: true,
    autoRefreshOnUpdate: false,
    notifyOnOffline: true,
    prefetchRoutes: ['/dashboard', '/support', '/students'],
  };

  const pwaManager = React.useRef<PWAManager | null>(null);
  const [isInstallable, setIsInstallable] = React.useState(false);
  const [isInstalled, setIsInstalled] = React.useState(false);
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    pwaManager.current = new PWAManager({ ...defaultConfig, ...config });
    
    // Check initial states
    setIsInstallable(pwaManager.current.isInstallable());
    setIsInstalled(pwaManager.current.isInstalledApp());

    // Listen for network changes
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for install prompt
    const handleBeforeInstallPrompt = () => {
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const showInstallPrompt = async () => {
    if (pwaManager.current) {
      const result = await pwaManager.current.showInstallPrompt();
      if (result) {
        setIsInstallable(false);
      }
      return result;
    }
    return false;
  };

  const storeOfflineRequest = async (requestData: any) => {
    if (pwaManager.current) {
      await pwaManager.current.storeOfflineRequest(requestData);
    }
  };

  const updateApp = async () => {
    if (pwaManager.current) {
      await pwaManager.current.updateApp();
    }
  };

  const getCacheStatus = async () => {
    if (pwaManager.current) {
      return await pwaManager.current.getCacheStatus();
    }
    return {};
  };

  const clearCache = async (cacheType?: string) => {
    if (pwaManager.current) {
      await pwaManager.current.clearCache(cacheType);
    }
  };

  const getConnectionInfo = () => {
    if (pwaManager.current) {
      return {
        online: pwaManager.current.isOnline(),
        type: pwaManager.current.getConnectionType(),
      };
    }
    return { online: navigator.onLine, type: 'unknown' };
  };

  return {
    isInstallable,
    isInstalled,
    isOnline,
    showInstallPrompt,
    storeOfflineRequest,
    updateApp,
    getCacheStatus,
    clearCache,
    getConnectionInfo,
  };
};

export default PWAManager;